import React, { CSSProperties, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import { BarSeriesType, LineSeriesType } from '@mui/x-charts';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import StackedLineChartIcon from '@mui/icons-material/StackedLineChart';
import { UserContext } from '../../../../../context/UserContext';
import AreaChartOutlinedIcon from '../../../../../components/AreaChartOutlinedIcon';
import { formatValue, getUniqueProperties } from '../../../../../helpers/helpers';
import { ChartType } from '../../../../../models/dashboard';
import { Bucket } from '../../../../../api/indicators';
import { DashboardFiltersContext } from '../../filters/DashboardFiltersContext';
import { formatPeriodLabel } from '../../../../../helpers/dateUtils';
import LineBarChartError from './LineBarChartError';
import LabeledChip from '../LabeledChip';
import LineBarChartBase from './LineBarChartBase';
import LineBarChartSkeleton from './LineBarChartSkeleton';
import ChartNoData from '../ChartNoData';


const selectStyle: CSSProperties = {
    fontFamily: 'Open Sans',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '24px',
    width: '210px',
};

export interface LineBarChartDataItem {
    dateTime: Date;
    barData: number;
    [lineData: string]: number | Date;
}

export interface LineBarChartProps {
    title: string;
    currency?: string;
    description?: string;
    textLink?: string;
    linkChart?: string;
    isLoading: boolean;
    isError: boolean;
    leftAxisLabel: string;
    rightAxisLabel: string;
    lineLabels?: string[];
    barLabel: string;
    data: LineBarChartDataItem[];
    dataCompared: LineBarChartDataItem[];
    bucket: Bucket;
    setBucket: (bucket: Bucket) => void;
    height: number;
    currentDateFrom: string;
    currentDateTo: string;
    comparedDateFrom: string;
    comparedDateTo: string;
}

const LineBarChartCompared = (
    {
        title,
        currency,
        description,
        isLoading,
        isError,
        leftAxisLabel,
        rightAxisLabel,
        textLink,
        linkChart,
        lineLabels,
        barLabel,
        data,
        dataCompared,
        bucket,
        setBucket,
        height,
        currentDateFrom,
        currentDateTo,
        comparedDateFrom,
        comparedDateTo
    }: LineBarChartProps
) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { locale } = useContext(UserContext);
    const [chartType, setChartType] = useState<ChartType>('line');
    const [linePlotSeries, setLinePlotSeries] = useState<(LineSeriesType | BarSeriesType)[]>([]);
    const [areaPlotSeries, setAreaPlotSeries] = useState<LineSeriesType[]>([]);
    const [series, setSeries] = useState<(LineSeriesType | BarSeriesType)[]>([]);
    const [linePlotSeriesCompared, setLinePlotSeriesCompared] = useState<(LineSeriesType | BarSeriesType)[]>([]);
    const [areaPlotSeriesCompared, setAreaPlotSeriesCompared] = useState<LineSeriesType[]>([]);
    const [seriesCompared, setSeriesCompared] = useState<(LineSeriesType | BarSeriesType)[]>([]);
    const colours: string[] = useMemo(() => [
        theme.palette.blue.main,
        theme.palette.cyan.main,
        theme.palette.teal.main,
        theme.palette.lightGreen.main,
        theme.palette.lime.main,
        theme.palette.yellow.main,
        theme.palette.orange.main,
        theme.palette.deepOrange.main,
        theme.palette.fucsia.main,
        theme.palette.purple.main,
    ], [theme]);
    const { openFilters } = React.useContext(DashboardFiltersContext)

    const buildLinePlotSeries = useCallback((dataProperties: string[]): (LineSeriesType | BarSeriesType)[] => {
        const barSeries: BarSeriesType = {
            id: 'barData',
            label: barLabel,
            type: 'bar',
            dataKey: 'barData',
            color: theme?.palette?.darkerGrey.light,
            yAxisId: 'y-axis-right-id'
        }
        const lineSeries: LineSeriesType[] = dataProperties.map((lineKey, index) => ({
            id: lineKey,
            label: lineLabels ? lineLabels[index] : lineKey,
            type: 'line',
            dataKey: lineKey,
            curve: 'linear',
            color: colours[index % colours.length],
            valueFormatter: (v: number | null) => formatValue(v, true, currency, locale)
        }))
        return [barSeries, ...lineSeries];
    }, [lineLabels, colours, currency, locale]);

    const buildAreaPlotSeries = useCallback(
        (dataProperties: string[]): LineSeriesType[] =>
            dataProperties.map((lineKey, index) => ({
                id: lineKey,
                label: lineLabels ? lineLabels[index] : lineKey,
                type: 'line',
                dataKey: lineKey,
                curve: 'linear',
                color: colours[index % colours.length],
                valueFormatter: (v: number | null) => formatValue(v, true, currency, locale),
                area: true
            })),
        [lineLabels, colours, currency, locale]
    );

    const noData = !data?.length;
    const noDataCompared = !dataCompared?.length;
    const noDataAtAll = noData && noDataCompared;
    const currentDateLabel = `${formatPeriodLabel(new Date(currentDateFrom), locale)} - ${formatPeriodLabel(new Date(currentDateTo), locale)}`;
    const comparedDateLabel = `${formatPeriodLabel(new Date(comparedDateFrom), locale)} - ${formatPeriodLabel(new Date(comparedDateTo), locale)}`;

    useEffect(() => {
        if (noData) return;

        const dataProperties = getUniqueProperties(data, ['dateTime', 'barData'])
        const lineSeries = buildLinePlotSeries(dataProperties)
        const areaSeries = buildAreaPlotSeries(dataProperties)

        setLinePlotSeries(lineSeries)
        setAreaPlotSeries(areaSeries)

        switch (chartType) {
            case 'line':
                setSeries(lineSeries)
                break;
            case 'area':
                setSeries(areaPlotSeries)
                break;
            default:
                setSeries(linePlotSeries)
                break;
        }
    }, [data]);

    useEffect(() => {
        if (noDataCompared) return;

        const dataProperties = getUniqueProperties(dataCompared, ['dateTime', 'barData'])
        const lineSeriesCompared = buildLinePlotSeries(dataProperties)
        const areaSeriesCompared = buildAreaPlotSeries(dataProperties)

        setLinePlotSeriesCompared(lineSeriesCompared)
        setAreaPlotSeriesCompared(areaSeriesCompared)

        switch (chartType) {
            case 'line':
                setSeriesCompared(lineSeriesCompared)
                break;
            case 'area':
                setSeriesCompared(areaPlotSeries)
                break;
            default:
                setSeriesCompared(linePlotSeries)
                break;
        }
    }, [dataCompared]);

    const toggleChart = (
        event: React.MouseEvent<HTMLElement>,
        newChartType: ChartType,
    ) => {
        if (newChartType !== null) {
            setChartType(newChartType);
            if (newChartType === 'line') {
                setSeries(linePlotSeries);
                setSeriesCompared(linePlotSeriesCompared);
            }
            if (newChartType === 'area') {
                setSeries(areaPlotSeries);
                setSeriesCompared(areaPlotSeriesCompared);
            }
        }
    };

    return (
        <Box>
            <Card
                elevation={0}
                sx={{
                    minWidth: '200px',
                    border: `solid 1px`,
                    borderColor: theme.palette.grey['12p'],
                    marginX: '8px',
                    padding: '12px 16px',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column', 
                    breakInside: 'avoid', 
                    pageBreakInside: 'avoid'
                }}
            >
                {isLoading && <LineBarChartSkeleton />}
                {!isLoading && (
                    <>
                        {isError && <LineBarChartError title={title} description={description} bucket={bucket} setBucket={setBucket} />}
                        {!isError && noDataAtAll && <ChartNoData height={height}  title={title} description={description} openFilters={openFilters} />}
                        {!isError && !noDataAtAll && (
                            <>
                                <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto', margin: '6px' }}>
                                    <Box>
                                        <Typography variant="h2">{title}</Typography>
                                        <Typography sx={{ fontSize: '14px', fontWeight: '400' }}>{description}</Typography>
                                    </Box>
                                    <>
                                        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                            <TextField
                                                size="medium"
                                                sx={selectStyle}
                                                select
                                                label={t("screens.dashboarding.charts.showBy")}
                                                value={bucket}
                                                id="period"
                                                onChange={(event) => setBucket(event.target.value as Bucket)}
                                                inputProps={{ 'data-qa-id': "filterPeriodOption" }}
                                            >
                                                <MenuItem value="hour">{t('components.select.hour')}</MenuItem>
                                                <MenuItem value="day">{t('components.select.days')}</MenuItem>
                                                <MenuItem value="week">{t('components.select.week')}</MenuItem>
                                            </TextField>
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto' }}>
                                            <ToggleButtonGroup color="primary" value={chartType} exclusive onChange={toggleChart}>
                                                <ToggleButton value="line" sx={{ border: 'none', borderRadius: '50% !important', margin: '5px' }}>
                                                    <StackedLineChartIcon />
                                                </ToggleButton>
                                                <ToggleButton value="area" sx={{ border: 'none', borderRadius: '50% !important', margin: '5px' }}>
                                                    <AreaChartOutlinedIcon />
                                                </ToggleButton>
                                            </ToggleButtonGroup>
                                        </Box>
                                    </>
                                </Box>
                                <>
                                    <Box sx={{ display: 'flex', flexDirection: 'row', marginLeft: '20px' }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '50%', marginRight: '10px' }} >
                                            {noData &&  <ChartNoData height={height}  openFilters={openFilters} />}
                                            {!noData &&
                                                <>
                                                    <LabeledChip label={currentDateLabel} color='primary.dark' background='secondary.main' />
                                                    <LineBarChartBase bucket={bucket} chartType={chartType} series={series} data={data} height={height} leftAxisLabel={leftAxisLabel} rightAxisLabel={rightAxisLabel} />
                                                </>
                                            }
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '50%', marginLeft: '10px' }} >
                                            {noDataCompared &&  <ChartNoData height={height}  openFilters={openFilters} />}
                                            {!noDataCompared &&
                                                <>
                                                    <LabeledChip label={comparedDateLabel} color={theme.palette.amber.dark} background={theme.palette.warning['190p'] || theme.palette.amber.light } />
                                                    <LineBarChartBase bucket={bucket} chartType={chartType} series={seriesCompared} data={dataCompared} height={height} leftAxisLabel={leftAxisLabel} rightAxisLabel={rightAxisLabel} />
                                                </>
                                            }
                                        </Box>
                                    </Box>
                                    {!!linkChart && (
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'auto' }}>
                                            <Button href={linkChart} sx={{ fontSize: '16px', fontWeight: '600', color: 'primary.main' }}>
                                                {textLink}
                                            </Button>
                                        </Box>
                                    )}
                                </>
                            </>
                        )}
                    </>
                )}
            </Card>
        </Box>


    );
}

export default LineBarChartCompared;
